import type { PublicAccountDetail } from "../types/public-accounts-api.types";
import type { NewPublicAccountFormData } from "../types/new-public-account.types";
import { getPaymentMethodFieldsForAccount } from "../constants/bankOptionsByCountry";
import { COUNTRY_CODE_TO_CURRENCY } from "../constants/public-accounts.constants";

const SD_KEYS_AS_FORM_TOP_LEVEL_BANK = new Set([
  "bank_name",
  "bankName",
  "account_type",
  "accountType",
  "tipoCuenta",
  "account_number",
  "accountNumber",
  "numeroCuenta",
]);

const SD_KEYS_CRYPTO = new Set([
  "wallet_address",
  "direccionWallet",
  "network",
  "network_name",
]);

function toSnakeCase(str: string): string {
  return str.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
}

function getStr(obj: Record<string, unknown> | null | undefined, ...keys: string[]): string {
  if (!obj || typeof obj !== "object") return "";
  for (const key of keys) {
    const v = obj[key];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
}

function getValueFromSd(sd: Record<string, unknown>, fieldId: string): string {
  const v = sd[fieldId];
  if (v !== undefined && v !== null) {
    const s = typeof v === "string" ? v.trim() : String(v);
    if (s !== "") return s;
  }
  const snake = toSnakeCase(fieldId);
  const w = sd[snake];
  if (w !== undefined && w !== null) {
    const s = typeof w === "string" ? w.trim() : String(w);
    if (s !== "") return s;
  }
  return "";
}

export function mapPublicAccountDetailToFormData(
  account: PublicAccountDetail
): NewPublicAccountFormData {
  const sd: Record<string, unknown> =
    account.structured_data && typeof account.structured_data === "object"
      ? (account.structured_data as Record<string, unknown>)
      : {};

  const country_code = account.country_code?.trim() ?? "";
  const rawBankFromSd = getStr(sd, "bank_name", "bankName");
  let bank_name =
    country_code === "ARG" && rawBankFromSd === "CBU / CVU"
      ? "CBU"
      : rawBankFromSd;
  const account_typeRaw = getStr(sd, "account_type", "accountType", "tipoCuenta");
  const account_type =
    account_typeRaw.toLowerCase() === "ahorros"
      ? "ahorros"
      : account_typeRaw.toLowerCase() === "corriente"
        ? "corriente"
        : account_typeRaw;
  const account_number = getStr(sd, "account_number", "accountNumber", "numeroCuenta");
  const wallet_address = getStr(sd, "wallet_address", "direccionWallet");
  const network = getStr(sd, "network", "network_name");

  const methodFieldsConfig = getPaymentMethodFieldsForAccount(
    country_code,
    bank_name
  );

  const methodFields: Record<string, string> = {};

  for (const key of Object.keys(sd)) {
    if (SD_KEYS_AS_FORM_TOP_LEVEL_BANK.has(key)) continue;
    if (SD_KEYS_CRYPTO.has(key)) continue;
    const v = sd[key];
    const s = typeof v === "string" ? v.trim() : v != null ? String(v) : "";
    if (key in methodFields && methodFields[key]) continue;
    methodFields[key] = s;
  }

  for (const field of methodFieldsConfig) {
    if (field.id === "tipoCuenta" || field.id === "numeroCuenta") continue;
    const value = getValueFromSd(sd, field.id);
    if (value !== "" || field.id in methodFields) {
      methodFields[field.id] = value || methodFields[field.id] || "";
    }
  }

  if (country_code === "ARG" && rawBankFromSd === "CBU / CVU") {
    const legacy = getValueFromSd(sd, "cbuCvu");
    if (legacy) {
      methodFields.cbu = legacy;
    }
    delete methodFields.cbuCvu;
  }

  const currency =
    (COUNTRY_CODE_TO_CURRENCY[country_code] ?? account.currency?.trim()) || "";

  return {
    country_code,
    currency,
    payment_method: account.payment_method,
    internal_alias: account.internal_alias?.trim() ?? "",
    notes: account.notes?.trim() ?? "",
    bank_name,
    account_type,
    account_number,
    wallet_address,
    network,
    attachments: [],
    existing_attachment_urls:
      account.attachment_urls?.filter((u): u is string => Boolean(u?.trim())) ?? [],
    methodFields,
  };
}
